import React, { useState, useEffect } from "react";
import styled from "styled-components";
import HamburgerIcon from "./HamburgerIcon";
import { SocialIcons } from "./SocialIcons";
import { Link } from "react-router-dom";

const menuItems = [
  { id: 1, label: "Services", link: "solutions", type: "scroll" },
  { id: 2, label: "About Us", link: "mbappe", type: "scroll" },
  { id: 3, label: "Entrepreneurs", link: "entrepreneurs", type: "scroll" },
  { id: 4, label: "Why Us?", link: "why-choose", type: "scroll" },
  { id: 5, label: "Contact Us", link: "contact", type: "scroll" },
  { id: 6, label: "Book a Call", link: "book-call", type: "modal" },
];

// const HamburgerIcon = styled.svg`
//   display: none;
//   cursor: pointer;
//   @media (max-width: 500px) {
//     display: block;
//   }

//   div {
//     width: 25px;
//     height: 3px;
//     background-color: white;
//     margin: 5px 0;
//   }
// `;

export function NavigationMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.menu-container') && 
          !event.target.closest('.hamburger-icon')) {
        setIsOpen(false);
      }
    };

    // Update isMobile state on window resize
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setIsOpen(false);
    }
  };

  const handleMenuItemClick = (item) => {
    if (item.type === "scroll") {
      scrollToSection(item.link);
    } else if (item.type === "modal") {
      // Trigger the modal by dispatching a custom event
      const event = new CustomEvent('openCalendlyModal');
      window.dispatchEvent(event);
      setIsOpen(false);
    } else {
      setIsOpen(false);
    }
  };

  return (
    <MenuWrapper>
      <HamburgerIconWrapper className="hamburger-icon">
        <HamburgerIcon toggleMenu={toggleMenu} />
      </HamburgerIconWrapper>
      
      <MenuContainer className="menu-container" isOpen={isOpen} isMobile={isMobile}>
        {menuItems.map((item) => (
          item.type === "route" ? (
            <MenuLink
              key={item.id}
              as={Link}
              to={item.link}
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </MenuLink>
          ) : item.type === "external" ? (
            <MenuLink
              key={item.id}
              as="a"
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </MenuLink>
          ) : (
            <MenuItem
              key={item.id}
              tabIndex="0"
              role="button"
              onClick={() => handleMenuItemClick(item)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleMenuItemClick(item);
                }
              }}
            >
              {item.label}
            </MenuItem>
          )
        ))}
        {isMobile && isOpen && <MobileSocialIcons><SocialIcons /></MobileSocialIcons>}
      </MenuContainer>
      
      {!isMobile && <DesktopSocialIcons><SocialIcons /></DesktopSocialIcons>}
    </MenuWrapper>
  );
}

const MenuWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const HamburgerIconWrapper = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 12px;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    transition: all 0.3s ease;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(5px);
    
    &:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
`;

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  color: var(--Light-Gray-1, #fdfdfd);
  text-transform: uppercase;
  font: 500 16px/1 Manrope, sans-serif;
  transition: all 0.3s ease;
  
  @media (max-width: 991px) {
    gap: 20px;
    font-size: 15px;
  }
  
  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
    position: absolute;
    top: 45px;
    right: 0;
    flex-direction: column;
    background: rgba(2, 73, 95, 0.92);
    padding: 22px 25px 18px;
    border-radius: 12px;
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(15px);
    align-items: flex-start;
    gap: 18px;
    min-width: 220px;
    z-index: 100;
    transform-origin: top right;
    animation: ${({ isOpen }) => (isOpen ? 'slideIn 0.25s ease forwards' : 'none')};
    border: 1px solid rgba(255, 255, 255, 0.1);
    
    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px) scale(0.98);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
  }
  
  @media (max-width: 500px) {
    padding: 18px 22px 15px;
    width: 230px;
  }
`;

const MenuItem = styled.div`
  cursor: pointer;
  padding: 10px 0;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: #fdfdfd;
    transition: width 0.3s ease;
  }
  
  &:hover {
    text-decoration: none;
    transform: translateX(4px);
    
    &:after {
      width: 100%;
    }
  }
  
  &:focus {
    outline: none;
    text-decoration: none;
    
    &:after {
      width: 100%;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    font-weight: 600;
    padding: 12px 0;
    
    &:before {
      content: '';
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.6);
      margin-right: 10px;
      transition: all 0.3s ease;
    }
    
    &:hover:before {
      background-color: #ffffff;
      transform: scale(1.2);
    }
    
    &:after {
      bottom: 8px;
      height: 1px;
    }
  }
`;

const DesktopSocialIcons = styled.div`
  display: flex;
  margin-left: 20px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileSocialIcons = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    margin-top: 20px;
    padding-top: 15px;
    width: 100%;
    justify-content: flex-end;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
  }
`;

const MenuLink = styled(MenuItem)`
  text-decoration: none;
  color: var(--Light-Gray-1, #fdfdfd);
  position: relative;
  
  &:hover {
    text-decoration: none;
    color: #fdfdfd;
  }
`;
